import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { api } from "../../services/api";

const BRAND = "#5656ff";
const DARK = "#0b0062";
const SECONDARY = "#4B4D63";

const FormatoCard = ({ value, selected, onClick, label, description }) => (
  <Box
    onClick={() => onClick(value)}
    sx={{
      flex: 1,
      p: 2,
      borderRadius: "10px",
      border: `1.5px solid ${selected ? BRAND : "rgba(75,77,99,0.18)"}`,
      bgcolor: selected ? "rgba(86,86,255,0.04)" : "white",
      cursor: "pointer",
      transition: "border-color 200ms, background-color 200ms",
      "&:hover": { borderColor: BRAND },
      display: "flex",
      flexDirection: "column",
      gap: 0.4,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          border: `2px solid ${selected ? BRAND : "rgba(75,77,99,0.4)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {selected && (
          <Box
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BRAND }}
          />
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 600,
          fontSize: "14px",
          color: DARK,
        }}
      >
        {label}
      </Typography>
    </Box>
    {description && (
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          color: SECONDARY,
          pl: "24px",
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

const NewDiseno = ({ onClose, onCreate }) => {
  const [loading, setLoading] = useState(true);
  const [tarifasTickets, setTarifasTickets] = useState([]);
  const [tarifasVouchers, setTarifasVouchers] = useState([]);
  const [selectedTarifa, setSelectedTarifa] = useState("");
  const [selectedFormato, setSelectedFormato] = useState(null);
  const [alertFormato, setAlertFormato] = useState(null);
  const [alertTarifa, setAlertTarifa] = useState(null);
  const [error, setError] = useState(null);
  const [sinTarifas, setSinTarifas] = useState(false);

  const history = useHistory();
  const param = useParams();

  useEffect(() => {
    const fetchTarifas = async () => {
      setLoading(true);
      try {
        const response = await api.tarifas.getTarifasSinDiseno(param.id);
        if (response.status === "success" && response.data.tarifas) {
          setTarifasTickets(response.data.tarifas.filter((t) => t.idtipo === 1));
          setTarifasVouchers(response.data.tarifas.filter((t) => t.idtipo === 2));
        } else {
          setSinTarifas(true);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTarifas();
  }, []);

  const createDiseno = async () => {
    let hasError = false;
    if (!selectedFormato) {
      setAlertFormato("Seleccioná un formato");
      hasError = true;
    }
    if (!selectedTarifa) {
      setAlertTarifa("Seleccioná una tarifa");
      hasError = true;
    }
    if (hasError) return;

    const json = {
      type: {
        type: selectedFormato,
        // Pulsera: 25,2cm x 2,5cm reales (25,2cm = 3cm pegamento + 11cm blanco + 11,2cm verde) a 96dpi
        width: selectedFormato === "pulsera" ? 952 : 480,
        height: selectedFormato === "pulsera" ? 94 : 240,
      },
      elements: [],
    };

    setLoading(true);
    try {
      const data = {
        idevento: parseInt(param.id),
        idtarifa: parseInt(selectedTarifa),
        json: JSON.stringify(json),
      };
      const response = await api.impresion.create(param.id, selectedTarifa, data);
      if (response.status === "error") throw new Error(response.message);
      onClose();
      onCreate(response.data.diseno.id);
    } catch (err) {
      console.error(err);
      setError(err.message);
      setLoading(false);
    }
  };

  const hasTarifas = tarifasTickets.length > 0 || tarifasVouchers.length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px", p: 0.5 } }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Bricolage Grotesque, sans-serif",
          fontWeight: 700,
          fontSize: "20px",
          color: DARK,
          pb: 0,
        }}
      >
        Creá un nuevo diseño
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : sinTarifas || !hasTarifas ? (
          <Alert
            severity="info"
            sx={{ borderRadius: "8px", fontFamily: "Inter, sans-serif", fontSize: "13px" }}
          >
            Todas tus tarifas ya tienen diseño. Creá una nueva tarifa o editá los diseños actuales.
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {/* Tarifa */}
            <FormControl fullWidth error={!!alertTarifa}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "13px",
                  color: BRAND,
                  mb: 0.75,
                }}
              >
                Seleccioná la tarifa a diseñar: *
              </Typography>
              <Select
                value={selectedTarifa}
                displayEmpty
                onChange={(e) => { setAlertTarifa(null); setSelectedTarifa(e.target.value); }}
                size="small"
                sx={{
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
                }}
                renderValue={(val) =>
                  val
                    ? [...tarifasTickets, ...tarifasVouchers].find((t) => t.id === val)?.concepto
                    : <span style={{ color: SECONDARY }}>Seleccioná una tarifa</span>
                }
              >
                {tarifasTickets.length > 0 && (
                  <ListSubheader sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: SECONDARY, lineHeight: "28px" }}>
                    Tickets
                  </ListSubheader>
                )}
                {tarifasTickets.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ fontFamily: "Inter, sans-serif", fontSize: "14px" }}>
                    {t.concepto}
                  </MenuItem>
                ))}
                {tarifasVouchers.length > 0 && (
                  <ListSubheader sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: SECONDARY, lineHeight: "28px" }}>
                    Vouchers
                  </ListSubheader>
                )}
                {tarifasVouchers.map((v) => (
                  <MenuItem key={v.id} value={v.id} sx={{ fontFamily: "Inter, sans-serif", fontSize: "14px" }}>
                    {v.concepto}
                  </MenuItem>
                ))}
              </Select>
              {alertTarifa && (
                <FormHelperText sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px" }}>
                  {alertTarifa}
                </FormHelperText>
              )}
            </FormControl>

            {/* Formato */}
            <FormControl error={!!alertFormato}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "13px",
                  color: alertFormato ? "error.main" : SECONDARY,
                  mb: 0.75,
                }}
              >
                Tipo de formato: *
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <FormatoCard
                  value="pulsera"
                  selected={selectedFormato === "pulsera"}
                  onClick={(v) => { setAlertFormato(null); setSelectedFormato(v); }}
                  label="Pulsera"
                />
                <FormatoCard
                  value="tarjeta"
                  selected={selectedFormato === "tarjeta"}
                  onClick={(v) => { setAlertFormato(null); setSelectedFormato(v); }}
                  label="Tarjeta"
                />
              </Stack>
              {alertFormato && (
                <FormHelperText sx={{ fontFamily: "Inter, sans-serif", fontSize: "11px", mt: 0.5 }}>
                  {alertFormato}
                </FormHelperText>
              )}
            </FormControl>

            {error && (
              <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "13px" }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {!loading && (sinTarifas || !hasTarifas) ? (
          <Button
            variant="contained"
            onClick={() => history.push(`/eventos/${param.id}/tarifas`)}
            sx={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: BRAND,
              px: 3,
              "&:hover": { bgcolor: "#4b4be8" },
            }}
          >
            Crear tarifa
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={loading}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                color: SECONDARY,
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                "&:hover": { bgcolor: "rgba(75,77,99,0.06)" },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={createDiseno}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: BRAND,
                px: 3,
                "&:hover": { bgcolor: "#4b4be8" },
                "&.Mui-disabled": { bgcolor: "rgba(86,86,255,0.3)", color: "white" },
              }}
            >
              Comenzar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewDiseno;